import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Delete } from "@mui/icons-material";
import { Button, Snackbar, styled, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Alert from "../common/Alert";
import ConfirmationDialog from "../common/ConfirmationDialog";
import Error from "../common/Error";
import Loading from "../common/Loading";
import { pages } from "../pages/pages";
import { LocationSessionState } from "../types";
import { secondsToTime } from "../utils/timeHelpers";
import ExerciseOverview from "./ExerciseOverview";

export const deleteSessionMutation = gql`
    mutation DeleteSession($id: ID!) {
        deleteSession(id: $id)
    }
`;

const Session: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [deleteMutation] = useMutation(deleteSessionMutation);
    const [isLoading, setIsLoading] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [open, setOpen] = React.useState(false);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setOpen(false);
    };

    const { session: dbSession } = (location.state as LocationSessionState) ?? { session: undefined };

    const handleDelete = (value?: boolean) => {
        if (value && dbSession) {
            setIsLoading(true);
            deleteMutation({
                variables: {
                    id: dbSession.id,
                },
                refetchQueries: ["GetSessions"],
            })
                .then(() => {
                    setIsLoading(false);
                    navigate(pages.profile, { state: { deletedSession: true } });
                })
                .catch(() => {
                    setIsLoading(false);
                    setOpen(true);
                });
        }
        setDialogOpen(false);
    };

    return (
        <Container>
            {isLoading && <Loading />}
            <Typography variant="h5" textAlign="center">
                {t("exercise_session")}
            </Typography>
            {dbSession ? (
                <>
                    <Typography textAlign="center">{format(new Date(dbSession.createdAt), "dd.MM.yyyy")}</Typography>
                    <Typography style={{ marginTop: 20 }}>
                        {t("duration")}: {secondsToTime(dbSession.durationTotal)} {t("minutes")}
                    </Typography>
                    <ExerciseOverview session={dbSession} />
                    <ButtonContainer>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => {
                                setDialogOpen(true);
                            }}
                        >
                            {t("delete_session")}
                        </Button>
                    </ButtonContainer>
                </>
            ) : (
                <Error text={t("error")} />
            )}
            <ConfirmationDialog open={dialogOpen} onClose={handleDelete} title="delete_session">
                {t("delete_session_content")}
            </ConfirmationDialog>
            <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
                    {t("error")}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Session;

const Container = styled("div")`
    padding: 30px;
`;

const ButtonContainer = styled("div")`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 20px;
`;
